import { useState, useEffect, useCallback } from 'react';
import { Reminder, ReminderFormData } from '../types/reminders';
import { useAuth } from '../context/AuthContext';
import { useNotifications } from './useNotifications';
import { remindersAPI } from '../services/api';

interface UseRemindersReturn {
  reminders: Reminder[];
  isLoading: boolean;
  createReminder: (data: ReminderFormData) => Promise<Reminder>;
  updateReminder: (id: string, data: Partial<ReminderFormData>) => Promise<Reminder>;
  deleteReminder: (id: string) => Promise<void>;
  toggleReminder: (id: string) => Promise<void>;
  checkPendingReminders: () => void;
}

export const useReminders = (): UseRemindersReturn => {
  const [reminders, setReminders] = useState<Reminder[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();
  const { showNotification } = useNotifications();

  // Calcular próxima fecha de envío
  const calculateNextSendTime = (reminderData: ReminderFormData | Reminder): Date => {
    const now = new Date();
    const [hours, minutes] = reminderData.time.split(':').map(Number);
    
    let nextDate = new Date();
    nextDate.setHours(hours, minutes, 0, 0);
    
    // Si ya pasó la hora de hoy, programar para mañana (para daily)
    if (nextDate <= now && reminderData.frequency === 'daily') {
      nextDate.setDate(nextDate.getDate() + 1);
    }
    
    // Para frecuencias más complejas
    switch (reminderData.frequency) {
      case 'weekly':
        if (reminderData.days_of_week && reminderData.days_of_week.length > 0) {
          // Encontrar el próximo día de la semana
          const currentDay = now.getDay();
          const nextDays = reminderData.days_of_week
            .map(day => day >= currentDay ? day : day + 7)
            .sort((a, b) => a - b);
          
          const nextDay = nextDays[0];
          const daysToAdd = nextDay - currentDay;
          nextDate.setDate(nextDate.getDate() + daysToAdd);
          
          if (daysToAdd === 0 && nextDate <= now) {
            // Si es hoy pero ya pasó la hora, ir al siguiente día de la semana
            const nextWeekDay = nextDays[1] || (nextDays[0] + 7);
            nextDate.setDate(nextDate.getDate() + (nextWeekDay - currentDay));
          }
        }
        break;
        
      case 'monthly':
        if (reminderData.days_of_month && reminderData.days_of_month.length > 0) {
          const currentDate = now.getDate();
          const nextDaysOfMonth = reminderData.days_of_month
            .filter(day => day >= currentDate || nextDate <= now)
            .sort((a, b) => a - b);
          
          if (nextDaysOfMonth.length === 0) {
            // Ir al próximo mes
            nextDate.setMonth(nextDate.getMonth() + 1);
            nextDate.setDate(reminderData.days_of_month[0]);
          } else {
            nextDate.setDate(nextDaysOfMonth[0]);
          }
        }
        break;
    }
    
    return nextDate;
  };

  // Crear recordatorio
  const createReminder = useCallback(async (data: ReminderFormData): Promise<Reminder> => {
    if (!user) throw new Error('Usuario no autenticado');
    
    try {
      const response = await remindersAPI.create(data);
      const newReminder = response.data.data.reminder;
      
      setReminders(prev => [...prev, newReminder]);
      return newReminder;
    } catch (error) {
      console.error('Error creando recordatorio:', error);
      throw error;
    }
  }, [user]);

  // Actualizar recordatorio
  const updateReminder = useCallback(async (id: string, data: Partial<ReminderFormData>): Promise<Reminder> => {
    if (!user) throw new Error('Usuario no autenticado');
    
    try {
      const response = await remindersAPI.update(parseInt(id), data);
      const updatedReminder = response.data.data.reminder;
      
      setReminders(prev => prev.map(r => r.id === id ? updatedReminder : r));
      return updatedReminder;
    } catch (error) {
      console.error('Error actualizando recordatorio:', error);
      throw error;
    }
  }, [user]);

  // Eliminar recordatorio
  const deleteReminder = useCallback(async (id: string): Promise<void> => {
    if (!user) return;
    
    try {
      await remindersAPI.delete(parseInt(id));
      setReminders(prev => prev.filter(r => r.id !== id));
    } catch (error) {
      console.error('Error eliminando recordatorio:', error);
      throw error;
    }
  }, [user]);

  // Alternar estado activo/inactivo
  const toggleReminder = useCallback(async (id: string): Promise<void> => {
    if (!user) return;
    
    try {
      const response = await remindersAPI.toggle(parseInt(id));
      const updatedReminder = response.data.data.reminder;
      
      setReminders(prev => prev.map(r => r.id === id ? updatedReminder : r));
    } catch (error) {
      console.error('Error cambiando estado del recordatorio:', error);
      throw error;
    }
  }, [user]);

  // Solo verificar recordatorios para actualizar el estado local - NO enviar notificaciones
  const checkPendingReminders = useCallback(() => {
    // Esta función ya no envía notificaciones
    // Solo se usa para mantener el estado local actualizado
    // Las notificaciones las maneja completamente el backend
    console.log('Estado de recordatorios actualizado. Notificaciones manejadas por el backend.');
  }, []);

  // Cargar recordatorios al inicializar
  useEffect(() => {
    if (!user) {
      setReminders([]);
      setIsLoading(false);
      return;
    }
    
    const loadReminders = async () => {
      try {
        setIsLoading(true);
        console.log('🔄 Cargando recordatorios para usuario:', user.name);
        const response = await remindersAPI.getAll();
        const loadedReminders = response.data.data.reminders;
        console.log('📋 Recordatorios cargados:', loadedReminders.length);
        console.log('📋 Recordatorios activos:', loadedReminders.filter((r: any) => r.enabled).length);
        setReminders(loadedReminders);
      } catch (error) {
        console.error('❌ Error cargando recordatorios:', error);
        setReminders([]);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadReminders();
  }, [user]);

  // YA NO verificamos recordatorios en el frontend
  // El backend se encarga de todo automáticamente
  useEffect(() => {
    console.log('🔔 Recordatorios manejados completamente por el backend');
  }, []);

  return {
    reminders,
    isLoading,
    createReminder,
    updateReminder,
    deleteReminder,
    toggleReminder,
    checkPendingReminders
  };
};